import { useEffect, useRef, useState, useMemo } from 'react';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import { fromLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import Overlay from 'ol/Overlay';
import 'ol/ol.css';

const LATITUD_CENTRO = 18.4861;
const LONGITUD_CENTRO = -69.9312;
const ZOOM = 13;

const STATUS_COLORS = {
    'Recibido': '#95a5a6',
    'Revisado': '#3498db',
    'Asignado': '#f39c12',
    'En proceso': '#e67e22',
    'Resuelto': '#27ae60',
    'Cerrado': '#7f8c8d'
};

const CATEGORY_LABELS = {
    iluminacion: 'Iluminacion',
    bancos: 'Bancos / Mobiliario',
    basura: 'Basura / Limpieza',
    vegetacion: 'Vegetacion / Poda',
    infraestructura: 'Infraestructura',
    otro: 'Otro'
};

const PRIORITY_COLORS = { baja: '#95a5a6', media: '#f39c12', alta: '#e74c3c' };

const MapaReportes = ({ reportes, sectors, onSelectReporte, onClose }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const popupRef = useRef(null);
    const overlayRef = useRef(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [showLegend, setShowLegend] = useState(false);
    const vectorSourceRef = useRef(null);

    // Construir lookup de sectores con coordenadas
    const sectorMap = useMemo(() => {
        const map = {};
        (sectors || []).forEach(s => {
            if (s.coordinates && s.coordinates.length > 0) {
                map[s._id] = {
                    name: s.name,
                    lat: s.coordinates[0][1],
                    lon: s.coordinates[0][0]
                };
            }
        });
        return map;
    }, [sectors]);

    useEffect(() => {
        if (!mapRef.current) return;

        const vectorSource = new VectorSource();
        vectorSourceRef.current = vectorSource;

        const vectorLayer = new VectorLayer({
            source: vectorSource,
            zIndex: 10
        });

        const map = new Map({
            target: mapRef.current,
            layers: [
                new TileLayer({ source: new OSM() }),
                vectorLayer
            ],
            view: new View({
                center: fromLonLat([LONGITUD_CENTRO, LATITUD_CENTRO]),
                zoom: ZOOM
            })
        });

        // Popup overlay
        const popup = document.createElement('div');
        popup.className = 'mapa-reportes-popup';
        popupRef.current = popup;
        mapRef.current.appendChild(popup);

        const overlay = new Overlay({
            element: popup,
            autoPan: true,
            autoPanAnimation: { duration: 250 }
        });
        overlayRef.current = overlay;
        map.addOverlay(overlay);

        // Click handler
        map.on('singleclick', (evt) => {
            const feature = map.forEachFeatureAtPixel(evt.pixel, f => f);
            if (feature) {
                const props = feature.getProperties();
                if (props.reporteId) {
                    const coords = feature.getGeometry().getCoordinates();
                    popup.innerHTML = `
                        <div class="mr-popup-content">
                            <div class="mr-popup-header">
                                <code>${props.trackingCode}</code>
                                <span class="mr-popup-status" style="background:${STATUS_COLORS[props.status]};color:white">${props.status}</span>
                            </div>
                            <h4>${props.reporterName}</h4>
                            <p>${props.description?.substring(0, 100)}${props.description?.length > 100 ? '...' : ''}</p>
                            <div class="mr-popup-cats">${(props.categories || []).map(c => `<span class="mr-popup-cat">${CATEGORY_LABELS[c] || c}</span>`).join('')}</div>
                            <p class="mr-popup-location">${props.locationName || 'Sin ubicacion'}</p>
                            ${props.priority === 'alta' ? '<span class="mr-popup-priority">PRIORIDAD ALTA</span>' : ''}
                            <button class="mr-popup-btn" onclick="document.dispatchEvent(new CustomEvent('openReporte', {detail:'${props.reporteId}'}))">Ver Detalle</button>
                        </div>
                    `;
                    overlay.setPosition(coords);
                }
            } else {
                overlay.setPosition(undefined);
            }
        });

        // Cursor pointer on features
        map.on('pointermove', (evt) => {
            const hit = map.forEachFeatureAtPixel(evt.pixel, () => true);
            map.getTargetElement().style.cursor = hit ? 'pointer' : '';
        });

        mapInstance.current = map;

        // Event listener for popup button
        const handleOpenReporte = (e) => {
            if (onSelectReporte) onSelectReporte(e.detail);
        };
        document.addEventListener('openReporte', handleOpenReporte);

        return () => {
            document.removeEventListener('openReporte', handleOpenReporte);
            map.setTarget(null);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Update markers when reportes/filters change
    useEffect(() => {
        if (!vectorSourceRef.current) return;

        vectorSourceRef.current.clear();

        let filtered = (reportes || []).filter(r => r.status !== 'Cerrado');
        if (statusFilter !== 'all') filtered = filtered.filter(r => r.status === statusFilter);
        if (categoryFilter !== 'all') filtered = filtered.filter(r => (r.categories || []).includes(categoryFilter));

        let hasCoords = false;

        filtered.forEach((r) => {
            let lat = null;
            let lon = null;

            // Intentar obtener coordenadas del sector
            if (r.sector && sectorMap[r.sector._id || r.sector]) {
                const s = sectorMap[r.sector._id || r.sector];
                lat = s.lat;
                lon = s.lon;
            }

            // Si no hay sector con coords, usar ubicacion heuristica
            // Distribuir puntos alrededor del centro para reportes sin coords exactas
            if (!lat || !lon) {
                // Generar posicion basada en hash del trackingCode
                const hash = (r.trackingCode || r._id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
                const angle = (hash % 360) * (Math.PI / 180);
                const distance = 0.005 + (hash % 100) * 0.00005;
                lat = LATITUD_CENTRO + Math.cos(angle) * distance;
                lon = LONGITUD_CENTRO + Math.sin(angle) * distance;
            }

            const statusColor = STATUS_COLORS[r.status] || '#95a5a6';
            const priorityColor = PRIORITY_COLORS[r.priority] || '#95a5a6';

            const feature = new Feature({
                geometry: new Point(fromLonLat([lon, lat])),
                reporteId: r._id,
                trackingCode: r.trackingCode,
                status: r.status,
                reporterName: r.reporterName,
                description: r.description,
                categories: r.categories,
                locationName: r.locationName || r.sector?.name || '',
                priority: r.priority
            });

            // Estilo principal: circulo con color de estado
            const outerRadius = r.priority === 'alta' ? 12 : 9;
            feature.setStyle(new Style({
                image: new CircleStyle({
                    radius: outerRadius,
                    fill: new Fill({ color: statusColor }),
                    stroke: new Stroke({
                        color: r.priority === 'alta' ? priorityColor : 'white',
                        width: r.priority === 'alta' ? 3 : 2
                    })
                }),
                text: new Text({
                    text: (r.categories || []).length > 1 ? String((r.categories || []).length) : '',
                    font: 'bold 10px sans-serif',
                    fill: new Fill({ color: 'white' }),
                    offsetY: 0
                })
            }));

            vectorSourceRef.current.addFeature(feature);
            hasCoords = true;
        });

        // Fit view to features
        if (hasCoords && mapInstance.current && vectorSourceRef.current.getFeatures().length > 0) {
            const extent = vectorSourceRef.current.getExtent();
            mapInstance.current.getView().fit(extent, { padding: [60, 60, 60, 60], maxZoom: 16, duration: 500 });
        }
    }, [reportes, statusFilter, categoryFilter, sectorMap]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="mapa-reportes-container">
            {/* Header */}
            <div className="mapa-reportes-header">
                <h3>Mapa de Reportes Ciudadanos</h3>
                <div className="mapa-reportes-controls">
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="mr-filter-select">
                        <option value="all">Todos los estados</option>
                        {Object.keys(STATUS_COLORS).filter(s => s !== 'Cerrado').map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="mr-filter-select">
                        <option value="all">Todas las categorias</option>
                        {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                    <button className="mr-legend-btn" onClick={() => setShowLegend(!showLegend)} title="Leyenda">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                        </svg>
                    </button>
                    {onClose && (
                        <button className="mr-close-btn" onClick={onClose}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Map */}
            <div ref={mapRef} className="mapa-reportes-map"></div>

            {/* Legend */}
            {showLegend && (
                <div className="mapa-reportes-legend">
                    <h4>Leyenda</h4>
                    <div className="mr-legend-section">
                        <strong>Estado</strong>
                        {Object.entries(STATUS_COLORS).map(([status, color]) => (
                            <div key={status} className="mr-legend-item">
                                <span className="mr-legend-dot" style={{ background: color }}></span>
                                {status}
                            </div>
                        ))}
                    </div>
                    <div className="mr-legend-section">
                        <strong>Prioridad</strong>
                        <div className="mr-legend-item">
                            <span className="mr-legend-dot" style={{ background: '#e74c3c', border: '2px solid #e74c3c' }}></span>
                            Alta (borde rojo, mas grande)
                        </div>
                    </div>
                    <div className="mr-legend-section">
                        <strong>Numero</strong>
                        <div className="mr-legend-item">
                            Numero dentro = cantidad de categorias
                        </div>
                    </div>
                </div>
            )}

            {/* Stats bar */}
            <div className="mapa-reportes-stats">
                <span>{(reportes || []).length} reportes</span>
                <span className="mr-stat-dot" style={{ background: STATUS_COLORS['Recibido'] }}></span>
                <span>{(reportes || []).filter(r => r.status === 'Recibido').length} recibidos</span>
                <span className="mr-stat-dot" style={{ background: STATUS_COLORS['En proceso'] }}></span>
                <span>{(reportes || []).filter(r => r.status === 'En proceso' || r.status === 'Asignado').length} en proceso</span>
                <span className="mr-stat-dot" style={{ background: STATUS_COLORS['Resuelto'] }}></span>
                <span>{(reportes || []).filter(r => r.status === 'Resuelto').length} resueltos</span>
            </div>
        </div>
    );
};

export default MapaReportes;
